// Landing route reached when the admin clicks "Return to admin" in the
// public app's impersonation banner. The public app opens
//   /impersonate/return?token=<adminReturnJWT>&returnTo=<path>
// with the return JWT it stored during handoff. We POST it to the API's
// restore endpoint, get the admin's own signed cookie token back, drop it
// into the `admin_token` cookie, hydrate Redux, and land the browser on
// `returnTo` (defaults to /user, the users list).
//
// This route is registered OUTSIDE the token-guarded Layout so it works
// when the admin's cookie is missing (e.g. cookie expired, or the admin
// used a different browser to click Return).

import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { API_URL } from '../../api';
import { login } from '../../store/mainReducer';

export default function ReturnHandler() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returnToken = params.get('token');
    const returnTo = params.get('returnTo') || '/user';

    if (!returnToken) {
      setError('Missing return token — please log in again.');
      setTimeout(() => navigate('/login'), 2500);
      return;
    }

    (async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/auth/impersonate/restore`,
          { returnToken }
        );
        const { token, userInfo } = res.data;
        const cookies = new Cookies();
        cookies.set('admin_token', token, { path: '/' });
        dispatch(login(userInfo));
        navigate(returnTo, { replace: true });
      } catch (err) {
        console.error('restoreAdmin error', err);
        const msg =
          err.response?.data?.message ||
          'Could not restore admin session — please log in again.';
        setError(msg);
        setTimeout(() => navigate('/login'), 2500);
      }
    })();
  }, [dispatch, location.search, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 3,
        textAlign: 'center',
      }}
    >
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Restoring your admin session…
          </Typography>
        </>
      )}
    </Box>
  );
}
